import {
  default as BackgroundImage
  // @ts-ignore
} from '@/assets/background.svg?react';
import {
  default as HyperlendLogo
  // @ts-ignore
} from '@/assets/logos/hyperlend.svg?react';
import {
  ActivityIcon,
  Card,
  CollapsableSidebar,
  CrossIcon,
  DangerIcon,
  Flex,
  FlexProps,
  HomeIcon,
  InfoCircleIcon,
  LiquidationIcon,
  MarketIcon,
  MenuIcon,
  PiggyBankIcon,
  useIsMobile,
  useToggle,
  WalletIcon
} from '@blockanalitica/ui';
import { ReactNode, useEffect, useMemo } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import styled, { css } from 'styled-components';
import UnavailableIcon from '../icons/UnavailableIcon';
import Footer from './Footer';
import MobileNavigation from './MobileNavigation';
import Navigation, { NavigationRoute } from './Navigation';
import { LinkButton } from '@/components';

const Background = styled(Flex)<FlexProps>`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100vh;
  z-index: -10;
  right: -25%;
`;

const BackgroundImg = styled(BackgroundImage)`
  width: 100%;
  height: 100%;
`;

const SidebarButton = styled(Flex)<FlexProps>`
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  height: 100%;

  ${({ theme }) => css`
    background-color: ${theme.colors.primary};
    color: ${theme.colors.hype.background70};
    border-radius: ${theme.sizes['-5']};
    gap: ${theme.space['xs']};
    font-size: ${theme.sizes['-1']};
    padding: ${theme.space['2xs']};

    &:hover {
      background-color: ${theme.colors.hype.textPrimaryHover};
    }
  `}
`;

const CloseButton = styled(Flex)<FlexProps>`
  cursor: pointer;
  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

const AlphaBackgroundLayer = styled.div`
  display: block;
  position: fixed;
  inset: 0;
  background-color: ${(props) => props.theme.colors.black65};
  z-index: 49;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

function createNavigationRoutes(): NavigationRoute[] {
  const routes = [
    {
      title: 'Home',
      linkTo: `/`,
      icon: <HomeIcon size="1" />
    },
    {
      title: 'Markets',
      linkTo: '/markets',
      icon: <MarketIcon size="1" />
    },
    {
      title: 'Wallets',
      linkTo: '/wallets',
      icon: <WalletIcon size="1" />
    },
    {
      title: 'Risk',
      linkTo: '#',
      icon: <DangerIcon size="1" />,
      subRoutes: [
        {
          title: 'Liquidations',
          linkTo: '/liquidations',
          icon: <LiquidationIcon size="1" />
        },
        {
          title: 'Parameters',
          linkTo: '/parameters',
          icon: <InfoCircleIcon size="1" />
        },
        {
          title: 'Underwater Wallets',
          linkTo: '/underwater-wallets',
          icon: <UnavailableIcon size="1" />
        }
      ]
    },
    {
      title: 'Protocol',
      linkTo: '#',
      icon: <UnavailableIcon size="1" />,
      subRoutes: [
        {
          title: 'Treasury',
          linkTo: '/treasury',
          icon: <PiggyBankIcon size="1" />
        },
        {
          title: 'Activity',
          linkTo: '/activity',
          icon: <ActivityIcon size="1" />
        }
      ]
    }
  ];

  return routes;
}

export default function Layout({
  children
}: {
  children?: ReactNode | ReactNode[];
}) {
  const [sidebarOpen, toggleSidebar] = useToggle();
  const location = useLocation();
  const isMobile = useIsMobile();

  const navigationRoutes = useMemo(() => createNavigationRoutes(), []);

  useEffect(() => {
    toggleSidebar(false);
  }, [location.pathname, toggleSidebar]);

  return (
    <>
      <CollapsableSidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        alignItems="flex-start">
        <Flex justifyContent="space-between" alignItems="center" width="100%">
          <HyperlendLogo height={24} alt="HyperLend" />

          <CloseButton alignSelf="center" onClick={() => toggleSidebar(false)}>
            <CrossIcon size="1" />
          </CloseButton>
        </Flex>

        <MobileNavigation routes={navigationRoutes} />

        <LinkButton
          variant="primary"
          size="small"
          as="a"
          // @ts-ignore
          href="https://app.hyperlend.finance"
          rel="noreferrer"
          target="_blank">
          Launch App
        </LinkButton>
      </CollapsableSidebar>

      <Card variant="menuCard" padding="0">
        <Flex
          flexGrow={1}
          flexShrink={1}
          flexBasis={['100%', 'auto']}
          margin="0 auto"
          width="100%"
          maxWidth={['100vw', '80rem']}
          flexDirection="column"
          justifyContent="space-between"
          padding="3xs s"
          gap="m">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            padding="2xs 0">
            <Link to="/">
              <Flex flexDirection="row" alignItems="center" gap="2xs">
                <HyperlendLogo height={isMobile ? 24 : 28} alt="HyperLend" />
              </Flex>
            </Link>

            {isMobile ? (
              <SidebarButton onClick={() => toggleSidebar()}>
                <MenuIcon size="2" />
              </SidebarButton>
            ) : (
              <>
                <Navigation routes={navigationRoutes} />

                <LinkButton
                  variant="primary"
                  size="small"
                  as="a"
                  // @ts-ignore
                  href="https://app.hyperlend.finance"
                  rel="noreferrer"
                  target="_blank">
                  Launch App
                </LinkButton>
              </>
            )}
          </Flex>
        </Flex>
      </Card>

      <Flex
        flexGrow={1}
        flexShrink={1}
        flexBasis={['100%', 'auto']}
        margin="0 auto"
        width="100%"
        maxWidth={['100vw', '80rem']}
        flexDirection="column"
        justifyContent="space-between"
        padding="s"
        gap="m">
        <main>{children ? children : <Outlet />}</main>

        <Footer />
      </Flex>

      {sidebarOpen ? (
        <AlphaBackgroundLayer onClick={() => toggleSidebar()} />
      ) : null}

      <Background>
        <BackgroundImg />
      </Background>
    </>
  );
}
